// Game controller: renderer + scene, the shot state machine, keeper AI, outcome detection,
// scoring, camera direction, slow motion and instant replays.
import * as THREE from 'three';
import {
  BALL_R, GOAL, SPOT_Z, PHYS, SHOTS_PER_ROUND, SPEED_MIN, SPEED_MAX, PERFECT_ZONE, OVERPOWER_FROM, AIM_LIMITS, keeperSkill,
} from './config.js';
import { BallBody, createWorld, stepBall, solveShot, simulateCrossing, spinForCurve } from './physics.js';
import { scoreShot, roundBonus, roundStatus, runStats, isPerfectPower } from './rules.js';
import { createStadium } from './stadium.js';
import { Keeper } from './keeper.js';
import { BallView, AimView, Particles } from './effects.js';

const PX = GOAL.halfWidth + GOAL.postR;
const BAR_Y = GOAL.height + GOAL.postR;
const KEEPER_SKINS = ['#8d5524', '#c68642', '#5a3a24', '#e0ac69', '#3d2616', '#f1c27d'];
const CURVES = [-1, -0.5, 0, 0.5, 1];
const AIM_CAM = { pos: new THREE.Vector3(0, 1.55, SPOT_Z + 4.4), look: new THREE.Vector3(0, 1.0, 0) };

const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
const damp = (k, dt) => 1 - Math.exp(-k * dt);

export class Game {
  constructor({ container, audio, ui, settings, onRunOver, onStateChange }) {
    this.container = container;
    this.audio = audio;
    this.ui = ui;
    this.settings = settings;
    this.onRunOver = onRunOver;
    this.onStateChange = onStateChange;
    this.state = 'boot';
    this.paused = false;
    this.time = 0;
    this.timeScale = 1;
    this.stateT = 0;
    this.aim = new THREE.Vector2(0.9, 1.3);
    this.curveIndex = 2;
    this.power = 0;
    this.chargeT = 0;
    this.charging = false;
    this.touch = false;
    this.frameTimes = [];
    this.running = false;
    this.raf = 0;
  }

  /** Creates the WebGL renderer. Throws a readable Error if WebGL is unavailable. */
  init() {
    let renderer;
    try {
      const probe = document.createElement('canvas');
      if (!probe.getContext('webgl2')) throw new Error('WebGL 2 is not available in this browser.');
      renderer = new THREE.WebGLRenderer({ antialias: this.resolvedQuality() !== 'low', powerPreference: 'high-performance' });
    } catch (e) {
      throw new Error(e?.message?.includes('WebGL') ? e.message : 'Your browser could not create a WebGL context.');
    }
    this.renderer = renderer;
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.05;
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFShadowMap;
    this.container.append(renderer.domElement);
    renderer.domElement.tabIndex = 0;
    renderer.domElement.addEventListener('webglcontextlost', (e) => { e.preventDefault(); this.ui.toast('Graphics context lost. Reload the page if the view stays black.', 6000); });

    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(50, 1, 0.1, 900);
    this.camera.position.copy(AIM_CAM.pos);
    this.camLook = AIM_CAM.look.clone();
    this.camPosTarget = AIM_CAM.pos.clone();
    this.camLookTarget = AIM_CAM.look.clone();
    this.aimLook = AIM_CAM.look.clone(); // adjusted for portrait screens in resize()
    this.shake = 0;

    const q = this.resolvedQuality();
    this.stadium = createStadium(this.scene, { quality: q });
    this.keeper = new Keeper(this.scene);
    this.ballBody = new BallBody();
    this.ballView = new BallView(this.scene);
    this.aimView = new AimView(this.scene);
    this.particles = new Particles(this.scene);
    this.world = createWorld();
    this.events = [];
    this.previewPath = [];
    this.raycaster = new THREE.Raycaster();
    this.goalPlane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0);
    this.tmpV = new THREE.Vector3();
    this.ndc = new THREE.Vector2();
    this.applyQuality();

    this.resetBall();
    this.keeper.setKit(keeperSkill(0).kit, KEEPER_SKINS[0]);
    this.onResize = () => this.resize();
    window.addEventListener('resize', this.onResize);
    window.visualViewport?.addEventListener('resize', this.onResize);
    this.resize();
    this.setState('menu');
  }

  resolvedQuality() {
    const q = this.settings.quality;
    if (q !== 'auto') return q;
    const coarse = matchMedia('(pointer: coarse)').matches;
    const weak = (navigator.hardwareConcurrency || 8) <= 4;
    return coarse || weak ? 'low' : 'high';
  }

  applyQuality() {
    const q = this.resolvedQuality();
    const cap = q === 'low' ? 1.25 : 2;
    this.pixelRatio = Math.min(window.devicePixelRatio || 1, this.adaptiveCap ?? cap, cap);
    this.renderer.setPixelRatio(this.pixelRatio);
    this.stadium.setQuality(q);
    this.resize();
  }

  resize() {
    const w = window.innerWidth;
    const h = window.innerHeight;
    if (!this.renderer || !w || !h) return;
    this.renderer.setSize(w, h, false);
    const aspect = w / h;
    this.camera.aspect = aspect;
    // keep ~11 m (the goal plus margin) visible horizontally on narrow portrait screens
    const dist = AIM_CAM.pos.z;
    const needV = THREE.MathUtils.radToDeg(2 * Math.atan(5.4 / dist / aspect));
    this.baseFov = clamp(Math.max(46, needV), 46, 80);
    this.camera.fov = this.baseFov;
    this.aimLook.copy(AIM_CAM.look);
    if (aspect < 0.9) this.aimLook.y = THREE.MathUtils.lerp(0.25, AIM_CAM.look.y, clamp((aspect - 0.45) / 0.45, 0, 1));
    if (this.state === 'aim' || this.state === 'charge' || this.state === 'ready' || this.state === 'intro') this.camLookTarget.copy(this.aimLook);
    this.camera.updateProjectionMatrix();
  }

  // ---------------------------------------------------------------- run / rounds

  startRun() {
    this.run = { round: 0, points: 0, goals: 0, shots: 0, roundsCleared: 0, streak: 0, sides: [] };
    this.paused = false;
    this.ui.setPoints(0);
    this.startRound();
  }

  startRound() {
    const r = this.run;
    this.skill = keeperSkill(r.round);
    r.roundShots = 0;
    r.roundGoals = 0;
    r.results = [];
    this.keeper.setKit(this.skill.kit, KEEPER_SKINS[r.round % KEEPER_SKINS.length]);
    this.keeper.shuffle = this.skill.shuffle;
    this.ui.setRound(r.round, this.skill.name);
    this.ui.setDots(r.results);
    this.prepareShot();
    this.setState('intro');
    const extra = r.round === 0 ? 'Score 3 of 5 to advance' : `Keeper level up: quicker reactions, longer dives${this.skill.readsPatterns ? ', reads your habits' : ''}`;
    this.ui.banner(`Round ${r.round + 1}`, { sub: `vs ${this.skill.name} · ${extra}`, kind: 'info', ms: 2100 });
  }

  prepareShot() {
    this.resetBall();
    this.keeper.reset(0);
    this.stadium.nets.reset();
    this.ballView.clearTrail();
    this.particles.clear();
    this.shot = null;
    this.replay = null;
    this.timeScale = 1;
    this.power = 0;
    this.charging = false;
    this.ui.setPower(0, null);
    this.ui.setCharging(false);
    this.camPosTarget.copy(AIM_CAM.pos);
    this.camLookTarget.copy(this.aimLook);
    // the keeper starts anticipating repeated choices from round 3
    this.expectedSide = 0;
    if (this.skill?.readsPatterns && this.run) {
      const last = this.run.sides.slice(-3);
      if (last.length === 3 && last[0] !== 0 && last.every((s) => s === last[0])) this.expectedSide = last[0];
    }
    this.keeper.setAnticipation(this.expectedSide * 0.38);
  }

  resetBall() {
    this.ballBody.reset(0, BALL_R, SPOT_Z);
    this.ballView.mesh.quaternion.identity();
    this.ballView.sync(this.ballBody, 0);
  }

  setState(s) {
    this.state = s;
    this.stateT = 0;
    const aiming = s === 'aim' || s === 'charge';
    this.aimView.setVisible(aiming);
    this.ui.setShootEnabled(aiming);
    this.updateHint();
    this.onStateChange?.(s);
  }

  updateHint() {
    const s = this.state;
    let text = '';
    if (s === 'aim') {
      text = this.touch ? 'Drag to aim · hold the button · release to shoot' : 'Aim with the mouse · hold click or Space · release to shoot';
      if (this.expectedSide) text = `The keeper is cheating to your ${this.expectedSide < 0 ? 'left' : 'right'}…`;
    } else if (s === 'charge') {
      text = 'Release in the green zone for a perfect strike';
    } else if (s === 'replay') {
      text = '';
    }
    this.ui.setHint(text);
  }

  // ---------------------------------------------------------------- input hooks

  aimAt(clientX, clientY) {
    if (!this.isAiming()) return;
    const rect = this.renderer.domElement.getBoundingClientRect();
    this.ndc.set(((clientX - rect.left) / rect.width) * 2 - 1, -((clientY - rect.top) / rect.height) * 2 + 1);
    this.raycaster.setFromCamera(this.ndc, this.camera);
    if (this.raycaster.ray.intersectPlane(this.goalPlane, this.tmpV)) this.setAim(this.tmpV.x, this.tmpV.y);
  }

  aimBy(dxPx, dyPx) {
    if (!this.isAiming()) return;
    // metres on the goal plane per screen pixel
    const h = this.renderer.domElement.clientHeight || 1;
    const dist = this.camera.position.z;
    const mpp = (2 * dist * Math.tan(THREE.MathUtils.degToRad(this.camera.fov / 2))) / h;
    this.setAim(this.aim.x + dxPx * mpp * 1.15, this.aim.y - dyPx * mpp * 1.15);
  }

  setAim(x, y) {
    this.aim.set(clamp(x, AIM_LIMITS.minX, AIM_LIMITS.maxX), clamp(y, AIM_LIMITS.minY, AIM_LIMITS.maxY));
  }

  isAiming() { return !this.paused && (this.state === 'aim' || this.state === 'charge'); }

  chargeStart() {
    if (this.paused || this.state !== 'aim') return;
    this.charging = true;
    this.chargeT = 0;
    this.keeper.getSet();
    this.ui.setCharging(true);
    this.setState('charge');
  }

  chargeEnd() {
    if (this.state !== 'charge' || !this.charging) return;
    this.charging = false;
    this.ui.setCharging(false);
    if (this.paused) { this.chargeCancel(); return; }
    this.kick(this.power);
  }

  chargeCancel() {
    if (this.state !== 'charge') return;
    this.charging = false;
    this.power = 0;
    this.ui.setPower(0, null);
    this.ui.setCharging(false);
    this.setState('aim');
  }

  curveStep(dir) {
    if (!this.isAiming()) return;
    this.setCurve(CURVES[clamp(this.curveIndex + dir, 0, CURVES.length - 1)]);
  }

  setCurve(value) {
    const i = CURVES.indexOf(value);
    if (i < 0) return;
    this.curveIndex = i;
    this.ui.setCurve(value);
    this.audio.tick();
  }

  get curve() { return CURVES[this.curveIndex]; }

  setTouch(t) { this.touch = t; this.updateHint(); }

  /** Skip intro banners / replays with a click, tap or key. */
  skip() {
    if (this.state === 'replay') this.endReplay();
    else if (this.state === 'intro' && this.stateT > 0.4) { this.ui.hideBanner(); this.stateT = 99; }
  }

  // ---------------------------------------------------------------- aiming helpers

  swayOffset(out) {
    const s = (this.skill?.sway ?? 0.1) * (this.state === 'charge' ? 1.5 : 1);
    const t = this.time;
    return out.set(
      s * (Math.sin(t * 1.1) * 0.7 + Math.sin(t * 2.3 + 1.3) * 0.3),
      s * 0.6 * (Math.sin(t * 1.7 + 0.5) * 0.7 + Math.sin(t * 3.1) * 0.3),
    );
  }

  /** Where the ball is actually sent for a given meter value: aim + sway, with overpower lifting it. */
  effectiveAim(power, out) {
    this.swayOffset(out).add(this.aim);
    if (power > OVERPOWER_FROM) out.y += ((power - OVERPOWER_FROM) / (1 - OVERPOWER_FROM)) * 1.4;
    return out;
  }

  speedFor(power) { return (SPEED_MIN + (SPEED_MAX - SPEED_MIN) * power) * (1 - 0.1 * Math.abs(this.curve)); }

  powerZone(p) {
    if (p > OVERPOWER_FROM) return 'over';
    if (p >= PERFECT_ZONE[0] && p <= PERFECT_ZONE[1]) return 'perfect';
    return null;
  }

  // ---------------------------------------------------------------- the shot

  /** Take a shot with meter value `power` (0..1). Also used by the test hooks. */
  kick(power) {
    const p = clamp(power, 0, 1);
    const target = this.effectiveAim(p, new THREE.Vector2());
    const speed = this.speedFor(p);
    const spin = spinForCurve(this.curve);
    const from = this.ballBody.pos.clone();
    const { vel, crossing } = solveShot(from, target.x, target.y, speed, spin);
    // what a keeper reading only the strike (not the bend) would expect
    const naive = simulateCrossing(from, vel, spinForCurve(0).setY(0)) || crossing;
    const side = Math.abs(target.x) < 0.9 ? 0 : Math.sign(target.x);
    let reaction = this.skill.reaction;
    let wrongFooted = false;
    if (this.expectedSide) {
      if (side === this.expectedSide) reaction = Math.max(0.06, reaction - 0.07);
      else { reaction += 0.12; wrongFooted = true; }
    }
    const fooled = Math.abs(this.curve) * this.skill.curveFooled;
    const trueCross = crossing || { x: target.x, y: target.y, t: 11 / speed };
    const read = {
      x: clamp(THREE.MathUtils.lerp(trueCross.x, naive.x, fooled), -PX - 0.4, PX + 0.4),
      y: clamp(THREE.MathUtils.lerp(trueCross.y, naive.y, fooled), 0.1, BAR_Y + 0.2),
    };

    this.ballBody.vel.copy(vel);
    this.ballBody.spin.copy(spin);
    this.ballBody.onGround = false;
    this.shot = {
      power: p, curve: this.curve, speed, target: target.clone(), trueCross, read, reaction, wrongFooted, side,
      t: 0, reacted: false, keeperTouched: false, hitFrame: null, crossed: null, resolved: null, resolvedAt: 0,
      lastZ: from.z, frames: [], netEvents: [], perfect: isPerfectPower(p),
    };
    this.run && this.run.sides.push(side);
    this.audio.kick(p);
    this.particles.turf(from, new THREE.Vector3(0, 0, 1), 18);
    if (!this.settings.reducedMotion) this.shake = 0.05;
    this.ui.setPower(p, this.powerZone(p));
    this.setState('flight');
  }

  /** One fixed physics step during a live shot. */
  stepShot(dt) {
    const s = this.shot;
    const b = this.ballBody;
    s.t += dt;
    if (!s.reacted && s.t >= s.reaction) {
      s.reacted = true;
      this.keeper.react(s.read, Math.max(0, s.trueCross.t - s.t), this.skill);
    } else if (s.reacted && !s.keeperTouched) {
      this.keeper.track(s.trueCross, dt, this.skill);
    }
    this.keeper.update(dt, this.time);
    this.world.keeper = this.keeper.world;
    this.events.length = 0;
    const prevZ = b.pos.z;
    const prevX = b.pos.x;
    const prevY = b.pos.y;
    stepBall(b, dt, this.world, this.events);
    for (const e of this.events) this.handleImpact(e);

    // first crossing of the goal-line plane (for scoring placement)
    if (!s.crossed && prevZ > 0 && b.pos.z <= 0) {
      const f = prevZ / (prevZ - b.pos.z || 1);
      s.crossed = { x: prevX + (b.pos.x - prevX) * f, y: prevY + (b.pos.y - prevY) * f };
    }
    if (s.resolved) return;

    const inFrame = Math.abs(b.pos.x) < GOAL.halfWidth && b.pos.y < GOAL.height;
    if (b.pos.z < -GOAL.postR - BALL_R && inFrame && b.pos.z > -GOAL.depth - 0.5) return this.resolve('goal');
    if (b.pos.z < -0.35 && !inFrame) return this.resolve(s.hitFrame ? 'post' : 'miss');
    const speed = b.vel.length();
    if (s.keeperTouched && ((b.vel.z > 0.4 && b.pos.z > 0.6) || speed < 1.2 || (s.t > 1.6 && b.pos.z > 0.2))) return this.resolve('save');
    if (s.hitFrame && b.vel.z > 0.3 && b.pos.z > 0.5) return this.resolve('post');
    if (speed < 0.3 && b.onGround) return this.resolve(s.keeperTouched ? 'save' : s.hitFrame ? 'post' : 'miss');
    if (s.t > 4) return this.resolve(s.keeperTouched ? 'save' : s.hitFrame ? 'post' : 'miss');
  }

  handleImpact(e) {
    const s = this.shot;
    switch (e.type) {
      case 'ground': this.audio.bounce(e.speed); break;
      case 'post':
      case 'bar':
        if (!s.hitFrame) s.hitFrame = e.type;
        this.audio.post(e.speed);
        if (!this.settings.reducedMotion) this.shake = Math.max(this.shake, 0.08);
        this.audio.crowd('ooh', 1);
        break;
      case 'net':
        this.stadium.nets.hit(e.panel, e.point, e.speed);
        s.netEvents.push({ t: s.t, panel: e.panel, point: e.point, speed: e.speed });
        this.audio.net(e.speed);
        break;
      case 'boards': this.audio.boards(e.speed); break;
      case 'keeper':
        if (!s.keeperTouched) { s.keeperTouched = true; this.audio.glove(e.speed); }
        break;
      default: break;
    }
  }

  resolve(outcome) {
    const s = this.shot;
    const r = this.run;
    s.resolved = outcome;
    s.resolvedAt = s.t;
    const cross = s.crossed || { x: this.ballBody.pos.x, y: this.ballBody.pos.y };
    if (r) {
      r.shots++;
      r.roundShots++;
      if (outcome === 'goal') { r.goals++; r.roundGoals++; r.streak++; } else r.streak = 0;
    }
    const scored = scoreShot({
      outcome, crossX: cross.x, crossY: cross.y, speed: s.speed, curve: s.curve, power: s.power,
      hitFrame: !!s.hitFrame, streak: r ? r.streak : 1, round: r ? r.round : 0,
    });
    s.points = scored.points;
    s.tags = scored.tags;
    if (r) {
      r.points += scored.points;
      r.results.push(outcome);
      this.ui.setDots(r.results);
      this.ui.setPoints(r.points, scored.points > 0);
    }

    const b = this.ballBody.pos;
    if (outcome === 'goal') {
      const mult = r ? `×${(1 + 0.5 * r.round).toString()}` : '';
      const has = (l) => scored.tags.some((t) => t.label === l);
      const title = has('Top corner') ? 'Top bins!' : has('Off the woodwork') ? 'In off the post!' : has('Rocket') ? 'What a hit!' : 'Goal!';
      this.ui.banner(title, { sub: `+${scored.points} pts ${r && r.round ? `(round ${mult})` : ''}`, tags: scored.tags.slice(1), kind: 'goal', ms: 2300 });
      this.audio.crowd('goal', 1);
      this.stadium.cheer(1);
      this.particles.confetti(new THREE.Vector3(clamp(b.x, -3, 3), 2.6, -1.5), this.settings.reducedMotion ? 120 : 320);
      this.keeper.dejected();
      if (!this.settings.reducedMotion) { this.slowmo = 0.7; }
    } else if (outcome === 'save') {
      const lines = ['Saved!', 'Denied!', 'Great save!'];
      this.ui.banner(lines[(r?.shots ?? 0) % lines.length], { sub: s.wrongFooted ? 'Unlucky: the keeper still got there' : `${this.skill.name} keeps it out`, kind: 'fail', ms: 2000 });
      this.audio.crowd('ooh', 1.1);
      this.keeper.celebrate();
    } else if (outcome === 'post') {
      this.ui.banner(s.hitFrame === 'bar' ? 'Crossbar!' : 'Off the post!', { sub: 'So close…', kind: 'fail', ms: 2000 });
      this.audio.crowd('groan', 1);
      this.keeper.celebrate();
    } else {
      const over = b.y > GOAL.height || cross.y > GOAL.height;
      this.ui.banner(over ? 'Over the bar!' : 'Wide!', { sub: s.power > OVERPOWER_FROM ? 'Too much power: it sailed' : 'Keep it on target', kind: 'fail', ms: 2000 });
      this.audio.crowd('groan', 0.9);
      this.keeper.celebrate();
    }
    this.onShotResolved?.(outcome, s);
  }

  /** Decide whether an outcome deserves an instant replay. */
  wantsReplay() {
    const s = this.shot;
    if (!this.settings.replays || !s || s.frames.length < 10) return false;
    if (s.resolved === 'goal') return s.tags.some((t) => ['Top corner', 'Bottom corner', 'Rocket', 'Off the woodwork'].includes(t.label));
    return s.resolved === 'save' || s.resolved === 'post';
  }

  afterResult() {
    if (this.wantsReplay()) { this.startReplay(); return; }
    this.nextShot();
  }

  nextShot() {
    const r = this.run;
    if (!r) { this.prepareShot(); this.setState('ready'); return; }
    const status = roundStatus(r.roundShots, r.roundGoals);
    if (status === 'continue') {
      this.prepareShot();
      this.setState('ready');
    } else if (status === 'cleared') {
      const bonus = roundBonus(r.round, r.roundGoals);
      r.points += bonus;
      r.roundsCleared++;
      this.ui.setPoints(r.points, true);
      this.ui.banner('Round cleared!', { sub: `+${bonus} bonus${r.roundGoals === SHOTS_PER_ROUND ? ' (perfect round)' : ''}`, kind: 'goal', ms: 2000 });
      this.audio.crowd('goal', 0.8);
      this.stadium.cheer(0.8);
      this.setState('cleared');
    } else {
      this.gameOver();
    }
  }

  gameOver() {
    this.setState('over');
    const stats = runStats(this.run);
    this.ui.hideBanner();
    this.onRunOver?.(this.run, stats);
  }

  // ---------------------------------------------------------------- replays

  recordFrame() {
    const s = this.shot;
    if (!s || s.frames.length > 900) return;
    const b = this.ballView.mesh;
    s.frames.push({
      t: s.t,
      ball: [b.position.x, b.position.y, b.position.z, b.quaternion.x, b.quaternion.y, b.quaternion.z, b.quaternion.w],
      keeper: this.keeper.snapshot([]),
    });
  }

  startReplay() {
    const s = this.shot;
    const end = s.resolvedAt + 0.9;
    const frames = s.frames.filter((f) => f.t <= end);
    const side = s.trueCross.x >= 0 ? -1 : 1;
    const angle = s.resolved === 'goal' ? 'behind' : 'side';
    this.replay = { frames, t: Math.max(0, s.resolvedAt - 1.0), end, speed: 0.4, i: 0, netI: 0, angle, side };
    this.stadium.nets.reset();
    this.ballView.clearTrail();
    this.ui.showReplay(true);
    this.ui.hideBanner();
    this.setState('replay');
  }

  updateReplay(dt) {
    const rp = this.replay;
    rp.t += dt * rp.speed;
    while (rp.i < rp.frames.length - 1 && rp.frames[rp.i + 1].t <= rp.t) rp.i++;
    const f = rp.frames[rp.i];
    const b = this.ballView.mesh;
    b.position.set(f.ball[0], f.ball[1], f.ball[2]);
    b.quaternion.set(f.ball[3], f.ball[4], f.ball[5], f.ball[6]);
    this.keeper.restore(f.keeper);
    const netEvents = this.shot.netEvents;
    while (rp.netI < netEvents.length && netEvents[rp.netI].t <= rp.t) {
      const n = netEvents[rp.netI++];
      this.stadium.nets.hit(n.panel, n.point, n.speed);
    }
    this.ballView.updateTrail(this.camera, rp.t < this.shot.resolvedAt, 20);
    // camera
    if (this.settings.reducedMotion) {
      this.camPosTarget.set(rp.side * 7.5, 2.2, 8);
      this.camLookTarget.set(0, 1.1, 1.5);
    } else if (rp.angle === 'behind') {
      this.camPosTarget.set(rp.side * 3.2, 1.5, -GOAL.depth - 2.6);
      this.camLookTarget.set(b.position.x * 0.5, 1.1, Math.max(0, b.position.z) + 2);
    } else {
      this.camPosTarget.set(rp.side * 8.5, 1.4, 4 + Math.min(4, b.position.z * 0.3));
      this.camLookTarget.set(b.position.x * 0.6, 1.0, b.position.z * 0.5);
    }
    if (rp.t >= rp.end || rp.i >= rp.frames.length - 1) this.endReplay();
  }

  endReplay() {
    if (this.state !== 'replay') return;
    this.ui.showReplay(false);
    this.replay = null;
    this.nextShot();
  }

  // ---------------------------------------------------------------- frame loop

  start() {
    if (this.running) return;
    this.running = true;
    this.last = performance.now();
    const loop = (now) => {
      if (!this.running) return;
      this.raf = requestAnimationFrame(loop);
      const dt = Math.max(0, Math.min(0.05, (now - this.last) / 1000)); // rAF time can precede performance.now()
      this.last = now;
      this.frame(dt);
    };
    this.raf = requestAnimationFrame(loop);
  }

  stop() { this.running = false; cancelAnimationFrame(this.raf); }

  frame(dt) {
    this.time += dt;
    this.trackPerformance(dt);
    if (!this.paused) this.update(dt);
    this.updateCamera(dt);
    this.stadium.update(this.time, this.paused ? 0 : dt);
    this.renderer.render(this.scene, this.camera);
  }

  update(dt) {
    this.stateT += dt;
    const s = this.state;
    // slow motion right after a goal
    if (this.slowmo > 0) { this.slowmo -= dt; this.timeScale = this.slowmo > 0 ? 0.35 : 1; }
    const simDt = dt * this.timeScale;

    if (s === 'menu') {
      this.keeper.update(dt, this.time);
      if (!this.settings.reducedMotion) {
        const a = this.time * 0.06;
        this.camPosTarget.set(Math.sin(a) * 8, 2.6 + Math.sin(a * 0.7) * 0.6, SPOT_Z + 4.5 + Math.cos(a) * 3);
      } else this.camPosTarget.set(4, 2.4, SPOT_Z + 6);
      this.camLookTarget.set(0, 1.2, 1);
    } else if (s === 'intro') {
      this.keeper.update(dt, this.time);
      if (this.stateT > 2.1) this.setState('ready');
    } else if (s === 'ready') {
      this.keeper.update(dt, this.time);
      if (this.stateT > 0.55) { this.audio.whistle(); this.setState('aim'); }
    } else if (s === 'aim' || s === 'charge') {
      const [kx, ky] = this.input?.keyAim() ?? [0, 0];
      if (kx || ky) this.setAim(this.aim.x + kx * 3.2 * dt, this.aim.y + ky * 2.4 * dt);
      if (s === 'charge') {
        this.chargeT += dt;
        // ping-pong meter: 0 -> 1 in 0.8 s and back down
        const cycle = (this.chargeT / 0.8) % 2;
        this.power = cycle < 1 ? cycle : 2 - cycle;
        this.ui.setPower(this.power, this.powerZone(this.power));
      }
      this.keeper.update(dt, this.time);
      this.updateAimView(s === 'charge' ? this.power : 0.75);
    } else if (s === 'flight' || s === 'result') {
      this.simulate(simDt);
      this.followCamera(dt);
      if (s === 'flight' && this.shot.resolved) this.setState('result');
      else if (s === 'result' && this.stateT > (this.shot.resolved === 'goal' ? 2.4 : 2.0)) this.afterResult();
    } else if (s === 'replay') {
      this.updateReplay(dt);
    } else if (s === 'cleared') {
      this.keeper.update(dt, this.time);
      if (this.stateT > 2.0) { this.run.round++; this.startRound(); }
    } else if (s === 'over') {
      this.keeper.update(dt, this.time);
    }

    this.particles.update(dt * this.timeScale);
  }

  simulate(simDt) {
    this.acc = (this.acc || 0) + simDt;
    let n = 0;
    while (this.acc >= PHYS.step && n < 60) {
      this.acc -= PHYS.step;
      n++;
      if (this.state === 'flight' || this.state === 'result') {
        if (this.shot.resolved && this.shot.t - this.shot.resolvedAt > 3) { this.keeper.update(PHYS.step, this.time); continue; }
        this.stepShot(PHYS.step);
      }
    }
    if (n === 60) this.acc = 0;
    this.ballView.sync(this.ballBody, simDt);
    this.ballView.updateTrail(this.camera, !this.shot.resolved || this.shot.t - this.shot.resolvedAt < 0.4, this.ballBody.vel.length());
    this.recordFrame();
  }

  updateAimView(previewPower) {
    const p = this.effectiveAim(previewPower, this.tmpAim || (this.tmpAim = new THREE.Vector2()));
    const pulse = this.state === 'charge' ? this.power : (Math.sin(this.time * 4) + 1) * 0.05;
    this.aimView.setTarget(p.x, p.y, pulse);
    const onTarget = Math.abs(p.x) < GOAL.halfWidth - BALL_R && p.y < GOAL.height - BALL_R;
    this.aimView.setColor(onTarget ? '#d8fd71' : '#ff4d5e');
    this.aimView.previewOn = this.settings.preview;
    this.aimView.dots.visible = this.settings.preview;
    if (this.settings.preview) {
      // recompute the preview at ~20 Hz: cheap and smooth enough
      if (!this.lastPreview || this.time - this.lastPreview > 0.05) {
        this.lastPreview = this.time;
        this.previewPath.length = 0;
        const from = this.ballBody.pos;
        const spin = spinForCurve(this.curve);
        const { vel } = solveShot(from, p.x, p.y, this.speedFor(previewPower), spin, 6);
        simulateCrossing(from, vel, spin, { path: this.previewPath, sampleEvery: 2 });
        this.aimView.setPath(this.previewPath, 0.42);
      }
    }
  }

  followCamera() {
    if (this.settings.reducedMotion) { this.camPosTarget.copy(AIM_CAM.pos); this.camLookTarget.copy(this.aimLook); return; }
    const b = this.ballBody.pos;
    const s = this.shot;
    if (s.resolved === 'goal' && this.state === 'result') {
      const k = Math.min(1, this.stateT / 1.6);
      this.camPosTarget.set(b.x * 0.25, 1.5 + k * 0.2, AIM_CAM.pos.z - 4.5 * k);
      this.camLookTarget.set(b.x * 0.5, 1.1, -0.5);
    } else {
      this.camPosTarget.set(clamp(b.x * 0.12, -0.6, 0.6), AIM_CAM.pos.y + 0.05, AIM_CAM.pos.z - 0.4);
      this.camLookTarget.set(clamp(b.x * 0.25, -1.2, 1.2), this.aimLook.y + 0.05 + clamp((b.y - 1) * 0.15, -0.1, 0.3), 0);
    }
  }

  updateCamera(dt) {
    const k = this.state === 'replay' ? 5 : this.state === 'menu' ? 1.5 : 3.2;
    this.camera.position.lerp(this.camPosTarget, damp(k, dt));
    this.camLook.lerp(this.camLookTarget, damp(k * 1.4, dt));
    this.camera.lookAt(this.camLook);
    if (this.shake > 0) {
      this.camera.position.x += (Math.random() - 0.5) * this.shake;
      this.camera.position.y += (Math.random() - 0.5) * this.shake;
      this.shake = Math.max(0, this.shake - dt * 0.4);
    }
  }

  /** Lower the pixel ratio automatically if frames are slow (Auto quality only). */
  trackPerformance(dt) {
    if (this.settings.quality !== 'auto' || this.paused || document.hidden) return;
    const ft = this.frameTimes;
    ft.push(dt);
    if (ft.length < 120) return;
    const avg = ft.reduce((a, b) => a + b, 0) / ft.length;
    ft.length = 0;
    if (avg > 0.028 && this.pixelRatio > 1) {
      this.adaptiveCap = Math.max(1, this.pixelRatio - 0.5);
      this.applyQuality();
    } else if (avg > 0.034 && !this.forcedLow) {
      this.forcedLow = true;
      this.stadium.setQuality('low');
    }
  }

  setPaused(p) {
    if (this.paused === p) return;
    this.paused = p;
    if (p && this.charging) this.chargeCancel();
    this.last = performance.now();
  }

  /** Back to the attract mode behind the menu. */
  toMenu() {
    this.run = null;
    this.paused = false;
    this.skill = keeperSkill(0);
    this.keeper.setKit(this.skill.kit, KEEPER_SKINS[0]);
    this.keeper.shuffle = 0.1;
    this.prepareShot();
    this.ui.hideBanner();
    this.ui.showReplay(false);
    this.setState('menu');
  }

  dispose() {
    this.stop();
    window.removeEventListener('resize', this.onResize);
    window.visualViewport?.removeEventListener('resize', this.onResize);
    this.stadium.dispose();
    this.keeper.dispose();
    this.ballView.dispose();
    this.renderer.dispose();
    this.renderer.domElement.remove();
  }
}
